import i18n, { TFunction } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next, Trans, useTranslation as useI18nTranslations } from 'react-i18next';

import { escape, isString } from 'common/utils';
import { configLocal, getStorageKey } from 'core/constants';
import { env } from 'core/constants/env.constants';
import { useCallback } from 'react';
import { BundledFallbackBackend, BundledFallbackBackendOptions } from './bundled-fallback.backend';
import { Language } from './i18n.model';
import { FileNamespace, NamespaceKeys } from './keys';
import { toBaseLanguage, toBcp47 } from './language.utils';

const defaultLanguageStorageKey = getStorageKey('language');
const defaultNamespace: FileNamespace = 'global';
const initialNamespaces: FileNamespace[] = [
  'common',
  'global',
  'operatorActions',
  'operators',
  'trainerActions',
  'trainings',
];

i18n.use(initReactI18next);
if (!configLocal.DEBUG_I18N_KEYS) {
  i18n.use(BundledFallbackBackend);
}
i18n.use(LanguageDetector);

i18n.init<BundledFallbackBackendOptions>({
  backend: {
    // S3 language service, completed key by key with the app-bundled copies.
    remote: HttpBackend,
    remoteOptions: {
      loadPath: `${env.VITE_LANGUAGE_API}/{{lng}}/{{ns}}.json`,
      // no-cache: json files are fetched at runtime and carry no content hash.
      requestOptions: { cache: 'no-cache' },
    },
    // Relative path (not the @locales alias): Vite's dynamic-import-vars only
    // resolves static resolve.alias entries, not vite-tsconfig-paths aliases.
    bundled: resourcesToBackend(
      (lng: string, ns: string) => import(`../../../locales/${lng}/${ns}.json`)
    ),
  },
  detection: {
    // Language comes from the user's stored preference, not the URL. Excluding
    // 'querystring' also avoids the detector reading window.location.search,
    // which throws when i18next's async retry fires outside a live DOM (tests).
    // navigator reports BCP-47 (en-US) but app locale codes are underscored
    // (en_US); without this the detected tag is kept and namespaces are fetched
    // under a locale that exists in neither the bucket nor the bundle.
    convertDetectedLanguage: 'Iso15897',
    order: ['cookie', 'localStorage', 'sessionStorage', 'navigator', 'htmlTag'],
    lookupCookie: defaultLanguageStorageKey,
    lookupLocalStorage: defaultLanguageStorageKey,
    lookupSessionStorage: defaultLanguageStorageKey,
  },
  react: {
    useSuspense: true,
  },
  // No supportedLngs: it would pin enablement to the build-time Language enum instead
  // of the bucket catalog. currentOnly stops a culture tag also fetching its base language.
  load: 'currentOnly',
  fallbackLng: Language.en_US,
  ns: initialNamespaces,
  defaultNS: defaultNamespace,
  interpolation: {
    escapeValue: false,
  },
});

// Keep the document language and the OneTrust consent banner aligned with the
// active language, covering both load-time application and in-app switches.
i18n.on('languageChanged', lng => {
  globalThis.document.documentElement.lang = toBcp47(lng);
  globalThis.Optanon?.changeLanguage?.(toBaseLanguage(lng));
});

const useTranslation = <NS extends FileNamespace>(ns: NS) => {
  const { t: rawT, i18n: i18nTranslation } = useI18nTranslations(ns);
  const translationFn = useCallback(
    (key: NamespaceKeys[NS], options?: Record<string, unknown>) => {
      if (options) {
        if (isString(options)) {
          options = escape(options) as unknown as Record<string, unknown>;
        } else {
          Object.values(options).forEach((o: unknown) => (isString(o) ? escape(o) : o));
        }
      }

      return rawT(key, options);
    },
    [rawT]
  );

  return {
    t: translationFn as typeof translationFn & TFunction,
    i18n: i18nTranslation,
  };
};

export { i18n, Trans, useTranslation };
